import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError, STATUS_META, brl } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ArrowLeft, Car, History, Loader2, CalendarClock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { OilSummary } from "@/components/OilChangeCard";

export default function VehicleHistory() {
  const { plate: raw } = useParams();
  const plate = decodeURIComponent(raw || "");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [warranty, setWarranty] = useState(null);

  const basePath = user?.role === "customer" ? "/cliente" : "/owner";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/orders/history/by-plate", { params: { plate } });
        setOrders(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
    if (user?.role !== "customer") {
      api.get(`/vehicles/by-plate/${encodeURIComponent(plate)}/warranty`).then(({ data }) => setWarranty(data)).catch(() => setWarranty({ items: [] }));
    }
  }, [plate, user?.role]);

  const vehicle = orders[0]?.vehicle;
  const lastOil = orders.filter((o) => o.oil_change).sort((a, b) => (b.oil_change.date || "").localeCompare(a.oil_change.date || ""))[0]?.oil_change;

  return (
    <div className="space-y-6 animate-fade-up">
      <button
        data-testid="history-back"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-green-brand transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Voltar
      </button>

      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-5 md:p-7">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-green-brand/15 border border-green-brand/40 flex items-center justify-center">
            <Car className="h-6 w-6 text-green-brand" />
          </div>
          <div>
            <div className="text-[11px] font-mono-caps text-zinc-500">Histórico do veículo</div>
            <h1 className="font-display text-3xl font-black uppercase text-zinc-100">{plate}</h1>
            {vehicle && (
              <div className="text-sm text-zinc-400 mt-0.5" translate="no">
                <span>
                  {[vehicle.make, vehicle.model, (vehicle.year || vehicle.year_model) ? `· ${[vehicle.year, vehicle.year_model].filter(Boolean).join("/")}` : null]
                    .filter(Boolean)
                    .join(" ")}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {lastOil && <div className="rounded-xl border border-zinc-800 bg-zinc-950/70 p-4"><OilSummary oil={lastOil} /></div>}
      {warranty && warranty.items?.length > 0 && (
        <div data-testid="warranty-block" className="rounded-xl border border-amber-800/60 bg-amber-950/20 p-4">
          <div className="flex items-center gap-2 mb-2"><ShieldCheck className="h-5 w-5 text-amber-300" /><h2 className="font-display text-base font-bold uppercase text-amber-200">Dentro da garantia</h2></div>
          <ul className="space-y-1 text-sm">
            {warranty.items.map((w, i) => (
              <li key={i} data-testid={`warranty-item-${i}`} className="flex justify-between gap-2">
                <span className="text-zinc-200">{w.description} <span className="text-[11px] text-zinc-500">({w.kind})</span></span>
                <span className="text-amber-200 whitespace-nowrap">até {new Date(w.until + "T00:00:00").toLocaleDateString("pt-BR")} <span className="text-[11px] text-zinc-500">({w.days_left} dias)</span></span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-zinc-500 mt-2">Avise o cliente ao abrir uma nova OS: ao fazer um retrabalho, considere usar o botão de retrabalho.</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-green-brand" /></div>
      ) : orders.length === 0 ? (
        <div data-testid="history-empty" className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950/40 p-10 text-center">
          <History className="h-10 w-10 mx-auto text-zinc-700" />
          <p className="mt-3 text-zinc-400 font-medium">Nenhum serviço registrado para esta placa.</p>
        </div>
      ) : (
        <ol className="relative border-l-2 border-zinc-800 ml-3 space-y-6">
          {orders.map((o) => {
            const date = o.created_at ? new Date(o.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }) : "";
            return (
              <li key={o.id} className="pl-5 relative" data-testid={`history-item-${o.id}`}>
                <span className="absolute -left-[9px] top-1 h-4 w-4 rounded-full bg-green-brand border-4 border-zinc-950" />
                <Link
                  to={`${basePath}/ordens/${o.id}`}
                  className="block rounded-xl border border-zinc-800 bg-zinc-950/70 hover:border-green-brand/50 transition-colors p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[11px] font-mono-caps text-zinc-500">{date}</div>
                      <div className="font-display text-xl font-bold text-zinc-100 mt-0.5">{o.description}</div>
                    </div>
                    <StatusBadge status={o.status} short />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <span className="px-2 py-1 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-400">
                      {o.photos?.length || 0} foto{(o.photos?.length || 0) === 1 ? "" : "s"}
                    </span>
                    <span className="px-2 py-1 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-400">
                      {o.checklist?.filter((c) => c.value !== "na").length || 0} itens no checklist
                    </span>
                    {o.budget && (
                      <span className={`px-2 py-1 rounded-md border ${o.budget.decision === "aprovado" ? "border-green-brand/40 text-green-300 bg-green-brand/10" : o.budget.decision === "recusado" ? "border-red-900 text-red-300 bg-red-950/30" : "border-amber-700 text-amber-300 bg-amber-950/30"}`}>
                        Orçamento {o.budget.decision} · {brl(o.budget.total)}
                      </span>
                    )}
                    {o.next_revision?.date && (
                      <span className="px-2 py-1 rounded-md border border-amber-brand/40 text-amber-300 bg-amber-brand/10 inline-flex items-center gap-1">
                        <CalendarClock className="h-3 w-3" />
                        Revisão {new Date(o.next_revision.date + "T00:00:00").toLocaleDateString("pt-BR")}
                      </span>
                    )}
                  </div>
                </Link>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
